<script lang="ts">
	//Svelte hooks
	import { beforeUpdate } from 'svelte';

	//Services
	import { format } from 'svelte-i18n';
	import { gen1CloudReq } from 'src/services/gen_1_reqs';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';

	//Helpers
	import { defaultTempFavPositions, real_id } from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';

	//Stores
	import { req_locked_devices } from 'src/stores/device-runtime/local-auth';
import { devices } from 'src/stores/lists/devices';
import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';

	//Design system components
	import { toast, TemperatureWheel, IconButton, Alert } from '@Shelly/shelly-design-system';

	//Components
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';

	export let device, status, settings, current;

	let manual = !settings?.thermostat?.[0]?.target_t?.enabled,
		tempUnit = settings?.thermostats?.[0]?.target_t?.units,
		calibrated = status?.thermostats?.[0]?.pos !== -1,
		value = manual
			? Number(Math.ceil(status?.thermostats?.[0]?.pos))
			: settings?.thermostats?.[0]?.target_t?.value,
		unit = settings?.thermostats?.[0]?.target_t?.units,
		btnStep = 0.1,
		step = settings?.thermostats?.[0]?.target_t?.units === 'C' ? btnStep : 1,
		cache_value = value,
		req_locked = false,
		isLoading = false,
		disabled = false;

	let min;
	let max;
	let pinLocked;
	let showModal = false,
		callbackValue;
	let sendTimeout: NodeJS.Timeout;
	let temp_fav_positions = device?.temp_fav_positions || defaultTempFavPositions(tempUnit);

	beforeUpdate(() => {
		manual = !settings?.thermostats?.[0]?.target_t?.enabled;
		unit = manual ? '%' : '°' + settings?.thermostats?.[0]?.target_t?.units;
		min = manual ? 0 : unit === '°F' ? 39 : 4;
		max = manual ? 100 : unit === '°F' ? 86 : 30;
		calibrated = status?.thermostats?.[0]?.pos !== -1;
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		if (value === cache_value) {
			value = manual
				? Number(Math.ceil(status?.thermostats?.[0]?.pos))
				: settings?.thermostats?.[0]?.target_t?.value;
			cache_value = value;
		}

		req_locked = $req_locked_devices.indexOf(real_id(device?.id)) !== -1;
	});

	const handleSetTemp = async (e: CustomEvent) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			value = e.detail.value;

			if (!calibrated) {
				if (value > max / 2) {
					value = max;
				} else {
					value = min;
				}
			}
			let res;
			disabled = true;
			req_locked_devices.add(real_id(device.id));

			if (manual) {
				//positioning control
				res = await gen1CloudReq(device, 'valve', { pos: value }, calibrated ? 20000 : 40000);
			} else {
				//temperature control
				res = await gen1CloudReq(
					device,
					'target_t',
					{ value: value },
					calibrated ? 20000 : 40000,
				);
			}

			req_locked_devices.remove(real_id(device.id));
			isLoading = false;
			if (typeof res === 'undefined') {
				disabled = false;
				// req_locked_devices.remove(real_id(device.id));
				value = cache_value;
				toast.open({ type: 'danger', title: $format('error_global') });
			} else if (res.isok) {
				disabled = false;
				cache_value = value;
				// req_locked_devices.remove(real_id(device.id));
				if (manual) {
					settings.thermostats[0].pos = value;
					toast.open({
						type: 'success',
						title:
							$format('position_set_scc_toast', {
								values: { value: `${value}°${tempUnit}` },
							}) || `Desired position was set to ${value}°${tempUnit}.`,
					});
				} else {
					settings.thermostats[0].target_t.value = value;
					toast.open({
						type: 'success',
						title:
							$format('temp_set_scc_toast', {
								values: { value: `${value}°${tempUnit}` },
							}) || `Desired temperature was set to ${value}°${tempUnit}.`,
					});
				}
				devices_settings.update_devices_settings(device.id, settings);
			}
		} else {
			callbackValue = e.detail.value;
			value = cache_value;
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleSetTemp({ detail: { value: callbackValue } } as CustomEvent);
	};

	const onChange = async (e: any) => {
		//debouncing on change for new buttons control
		clearTimeout(sendTimeout);
		sendTimeout = setTimeout(handleSetTemp, 1000, e);
	};
	const a11yProps = {
		tempWheelAriaLabel: $format('label_temperature', { default: 'Temperature' }),
		thumbAriaLabel: $format('label_temperature_thumb', { default: 'Temperature thumb' }),
		incrementBtnAriaLabel: $format('button_increase_temperature', {
			default: 'Increase temperature',
		}),
		decrementBtnAriaLabel: $format('button_decrease_temperature', {
			default: 'Decrease temperature',
		}),
	};
</script>

<!-- Dimitar said we will use this wheel, also will not use step 10 for percents -->
<TemperatureWheel
	{a11yProps}
	class="mb-m"
	valuePrefix={unit}
	bind:value
	{min}
	{max}
	on:value_change={onChange}
	on:move={() => {
		if ($os === 'IOS') {
			hapticFeedback('selection');
		}
	}}
	icon="fa fa-thermometer-three-quarters"
	type={'temp'}
	{isLoading}
	{step}
	{btnStep}
	current={{
		value: current,
		description: $format('label_current_temperature', {
			default: 'Current temperature',
		}),
	}}
/>
<div class="d-flex justify-content-center mb-m">
	{#each temp_fav_positions as pos}
		<IconButton
			id={`temp-fav-position-${pos.mode}`}
			aria-labelledby={`temp-fav-position-${pos.mode}`}
			title={$format(`temp_fav_position_${pos.mode}`, {
				default: capFirstChar(pos.mode),
			})}
			class="ml-xs mr-xs"
			disabled={!pos?.active}
			icon={getWorkingModeIcon(pos.mode)}
			variant={`${pos?.active && +value === +pos.value ? 'primary' : 'secondary'}`}
			on:click={() => onChange({ detail: { value: +pos.value } })}
		/>
	{/each}
</div>
{#if !device?.shared && temp_fav_positions?.filter((pos) => pos.active).length == 0}
	<Alert
		type="info"
		class="mb-s"
		heading={$format('alert_info_heading_temp_fav_positions', {
			default: 'You can enable or disable temperature favorite positions from sensor settings',
		})}
		cta={{
			title: $format('button_enable_disable', {
				default: 'Enable/Disable',
			}),
		}}
		on:cta_click={() => {
			navigateToSetting(device.id, 'sensor_settings', 'temperature_favorite_positions');
		}}
	/>
{/if}
{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
